<template>
    <div class="py-10">
        <div>
            <div class="text-center">
                <div class="normal-container">
                    <div class="mb-11 text-start lg:mb-24">
                        <ShopwareBreadcrumbs :breadcrumbPrefixEnabled="false" />
                    </div>
                    <div class="brands-sanity-content mb-14 lg:flex lg:justify-center">
                        <div class="lg:w-1/2">
                            <SanityContent :blocks="body" />
                        </div>
                    </div>
                    <div class="mt-5 mb-14 flex flex-wrap gap-4 lg:justify-center lg:gap-6">
                        <template v-for="(letter, index) in alphabet">
                            <button
                                @click="invokeNavigate(allData, index)"
                                class="font-monument-extended border-b-2 border-transparent pb-1 text-xl uppercase duration-300 hover:border-green-600">
                                {{ letter }}
                            </button>
                        </template>

                        <button
                            @click="scrollToLetter('#')"
                            class="font-monument-extended border-b-2 border-transparent pb-1 text-xl duration-300 hover:border-green-600">
                            #
                        </button>
                    </div>
                </div>
                <div class="normal-container mb-20 flex flex-wrap justify-center gap-4 px-5 lg:px-0">
                    <button
                        @click="genderFilter('all')"
                        :class="{ '!bg-green-600 font-bold': selectedGender === 'all' }"
                        class="!w-max rounded-full bg-green-300 px-8 py-4 text-base leading-none">
                        {{ snippets['headless.brands.everything'] }}
                    </button>
                    <button
                        @click="genderFilter('dames')"
                        :class="{ '!bg-green-600 font-bold': selectedGender === 'dames' }"
                        class="!w-max rounded-full bg-green-300 px-8 py-4 text-base leading-none">
                        {{ snippets['headless.brands.ladies'] }}
                    </button>
                    <button
                        @click="genderFilter('heren')"
                        :class="{ '!bg-green-600 font-bold': selectedGender === 'heren' }"
                        class="!w-max rounded-full bg-green-300 px-8 py-4 text-base leading-none">
                        {{ snippets['headless.brands.men'] }}
                    </button>
                </div>
            </div>
            <div class="normal-container !mx-4 lg:grid lg:grid-cols-3 lg:gap-x-24 xl:!mx-auto">
                <template v-for="(brands, letter) in allData" :key="letter">
                    <div
                        v-if="brands.length > 0"
                        :id="letter"
                        class="mb-10 flex scroll-mt-5 gap-4 lg:mb-40 lg:scroll-mt-44 lg:gap-9">
                        <h2
                            class="flex h-20 max-h-16 w-16 max-w-12 items-center justify-center rounded-full bg-green-500 text-xl uppercase lg:max-h-20 lg:max-w-16 lg:text-2xl">
                            {{ letter }}
                        </h2>
                        <div class="flex flex-1 flex-col pt-6 lg:pt-6">
                            <p class="font-monument-extended text-lg lg:text-xl" v-if="brands.length === 0">
                                Er zijn nog geen merken met deze letter.
                            </p>
                            <template v-for="brand in brands" :key="brand.id">
                                <NuxtLink
                                    :to="getRelativeUrl(brand.url)"
                                    class="group font-monument-extended mb-2 inline-block w-full text-base lg:text-xl">
                                    <span
                                        class="w-max border-b-2 border-transparent pb-1.5 duration-300 group-hover:border-green-600"
                                        >{{ brand.name }}</span
                                    >
                                </NuxtLink>
                            </template>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, nextTick } from 'vue';
    import { overwriteLatestPageHistory } from '~/composables/usePageHistoryStorage';

    defineProps({
        body: Object,
    });

    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;

    const alphabet = ref('abcdefghijklmnopqrstuvwxyz'.split(''));

    const config = useRuntimeConfig();
    const BASE_URL = `${config.public.shopwareEndpoint}brands`;
    const sw_access_key = `${config.public.shopwareToken}`;
    const route = useRoute();
    onMounted(() => {
        overwriteLatestPageHistory({
            key: route.path,
            url: route.fullPath,
            type: 'manufacturerListingPage',
            priority: 2, // higher priority to prevent productListing to overwrite the type
        });
    });

    function brandsByLetter(res: any) {
        try {
            const brands = {};
            alphabet.value.forEach(letter => {
                brands[letter] = [];
            });
            brands['#'] = [];
            if (res) {
                res.forEach(brand => {
                    if (brand && brand.name) {
                        const firstLetter = brand.name[0].toLowerCase();
                        if (alphabet.value.includes(firstLetter)) {
                            brands[firstLetter].push(brand);
                        } else {
                            brands['#'].push(brand);
                        }
                    }
                });
            }
            return brands;
        } catch (err) {
            console.error(err);
            return {};
        }
    }

    const selectedGender = ref('all');

    const brandsFetchUrl = computed(() => {
        return selectedGender.value === 'all' ? BASE_URL : `${BASE_URL}/${selectedGender.value}`;
    });

    const { data: brandsRaw } = await useFetch(brandsFetchUrl, {
        headers: {
            'sw-access-key': sw_access_key,
        },
    });

    const allData = computed(() => brandsByLetter(brandsRaw.value));

    function genderFilter(value: string) {
        selectedGender.value = value.toLowerCase();
    }

    function scrollToLetter(letter: string) {
        if (!process.client) return;

        const element = document.getElementById(letter);
        if (!element) return;

        const HEADER_HEIGHT = 150;

        // Scroll window
        const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementTop - HEADER_HEIGHT;

        window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth',
        });
    }

    function invokeNavigate(allData: any, index: any) {
        const current = alphabet.value[index];
        if (allData[current] && allData[current].length > 0) {
            nextTick(() => {
                scrollToLetter(current);
            });
        } else {
            const nextLetter = alphabet.value[index + 1];
            if (allData[nextLetter] && allData[nextLetter].length > 0) {
                nextTick(() => {
                    scrollToLetter(nextLetter);
                });
            } else {
                const prevLetter = alphabet.value[index - 1];
                if (allData[prevLetter] && allData[prevLetter].length > 0) {
                    nextTick(() => {
                        scrollToLetter(prevLetter);
                    });
                }
            }
        }
    }

    function getRelativeUrl(url: string): string {
        if (!url) return '/';
        try {
            // Only use URL parser for URLs with a real protocol (e.g. https://...)
            if (!url.includes('://')) throw new Error('No protocol');
            const urlObj = new URL(url);
            return urlObj.pathname;
        } catch {
            // Strip host and port (e.g. "localhost:3000/nl/merken/brand")
            const path = url.replace(/^[^/]*:\d+/, '');
            return path.startsWith('/') ? path : `/${path}`;
        }
    }
</script>
